import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { PortalHeader } from "@/components/PortalHeader";
import { EntityDetailsSection } from "@/components/EntityDetailsSection";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader2 } from "lucide-react";
import { delinkParticipant, reVerifyParticipant, verifyEndUserDetails } from "@/services/onBoardingFormServices";
import { ReusableDropdown } from "@/components/ui/re-usable-dropdown";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { countryOptions } from "./LinkingRequest";
import { FailureMessage, SuccessMessage } from "@/components/SuccessMessage";
import {ASP_ACCREDIATION_NUMBER} from "@/config/env";

const formSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  TIN: z
    .string()
    .min(10, "TIN must be 10 digits")
    .regex(/^1\d{8,}$/, "TIN must start with '1'"),
  countryCode: z
    .string()
    .min(1, "Country code is required"),
  mobile: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^\d{9,10}$/.test(val),
      "Mobile number must be 9-10 digits"
    ),
  actionType: z.enum(["reverify", "delink"]).default("reverify"),
});
const enitityDetailsFormSchema = z.object({
  TIN: z
    .string()
    .min(10, "TIN must be 10 digits")
    .regex(/^1\d{8,}$/, "TIN must start with '1'"),
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  legalType: z.string(),
  entityNameAr: z.string(),
  entityNameEn: z.string(),
  vatTrn: z.string(),
  effectiveDate: z.string(),
  mobile: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^[0-9]+$/.test(val),
      "Only digits are allowed"
    )
    .refine(
      (val) => !val || val.length === 10,
      "Mobile number must be exactly 10 digits"
    ),
  msgDesc: z.string(),
});

const ReverifyingOrDeLinkingRequest = () => {
  const navigate = useNavigate();
  const { toast, dismiss } = useToast();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [stepsStatus, setStepsStatus] = useState<{ step1: string, step2: string }>({ step1: '', step2: '' });
  const [onBoardingSuccess, setOnBoardingSuccess] = useState({ success: false, failure: false });
  const [isVerified, setIsVerified] = useState(false);
  const [declaration, setDeclaration] = useState(false);
  const [emarataxToken, setEmaraTaxToken] = useState('');
  const [TIN, setTIN] = useState('');
  const { search, pathname } = useLocation();
  const [verificationStatus, setVerificationStatus] = useState(false);
  const [isVerifyDisabled,setIsVerifyDisabled]=useState(false);
  const [isSubmitDisabled,setIsSubmitDisabled]=useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      email: "",
      TIN: TIN,
      countryCode: "AE",
      mobile: "",
      actionType: "reverify",
    },
  });
  const entityDetailsForm = useForm<z.infer<typeof enitityDetailsFormSchema>>({
    resolver: zodResolver(enitityDetailsFormSchema),
    mode: "onChange",
    defaultValues: {
      email: "",
      TIN: "",
      legalType: "",
      entityNameAr: "",
      entityNameEn: "",
      vatTrn: "",
      effectiveDate: "",
      mobile: "",
      msgDesc: "",
    },
  });
  const { handleSubmit, trigger, watch, setValue, reset, formState: { errors } } = form;
  const { handleSubmit: entitySubmit, watch: entityWatch, setValue: entitySetValue, reset: entityReset, formState: { errors: entityErrors } } = entityDetailsForm;
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let code = params.get("authcode");
    let TIN = params.get("TIN");
    if (code && TIN) {
      setValue("TIN", TIN);
      setEmaraTaxToken(code);
      setTIN(TIN);
      params.delete("authcode");
      params.delete("TIN");
      navigate(`${pathname}${params.toString() ? `?${params.toString()}` : ""}`, {
        replace: true
      });
    }
  }, [search, pathname, navigate]);

  const [entityDetails, setEntityDetails] = useState(null);

  const email = watch("email");

  useEffect(() => {
    const isEmailValid = email && !errors.email;
    setIsVerifyDisabled(!isEmailValid);
  }, [email, errors.email]);

  const verifyEmail = entityWatch("email");
  const verifyMobile = entityWatch("mobile");
  useEffect(() => {
    const isEmailValid = verifyEmail && !entityErrors.email;
    const isMobileValid = !verifyMobile || (verifyMobile && !entityErrors.mobile);
    setIsSubmitDisabled(!(isEmailValid && isMobileValid));
  }, [verifyEmail, verifyMobile, entityErrors.email, entityErrors.mobile]);

  const handleVerify = async (data: any) => {
    const isValid = await form.trigger();
    if (!isValid) return;
    if (!emarataxToken) {
      toast({
        title: "",
        description: "EmaraTax Token Can't Be Null !!",
        variant: "error",
        duration: 10000
      });
      return;
    }
    setIsVerifying(true);
    let payload = {
      "TIN": data.TIN,
      "email": data.email,
      "mobile": data.mobile,
      "emarataxToken": emarataxToken,
      "aspId": ASP_ACCREDIATION_NUMBER
    }
    await verifyEndUserDetails(payload).then(res => {
      if (res.success) {
        if (res.data) {
          setEntityDetails(res.data);
          entityDetailsForm.reset({ ...res.data });
          if (res?.data?.verificationStatus === 'True') {
            toast({
              title: "",
              description: `${watch('actionType')==='reverify'?'Reverification':'Delinking'} Failed, Please try again !!`,
              variant: "error",
              duration: 10000
            });
            setIsVerified(true);
            setVerificationStatus(false);
          }
          else if (res?.data?.verificationStatus === 'False') {
            setIsVerified(true);
            setVerificationStatus(true);
          }
        } else {
          toast({
            title: "",
            description: `${watch('actionType')==='reverify'?'Reverification':'Delinking'} Failed, Please try again !!`,
            variant: "error",
            duration: 10000,
          });
          setEntityDetails(null);
          setIsVerified(false);
          setVerificationStatus(false);
        }
      } else {
        toast({
          title: "",
          description: res?.message || res?.data?.message || `${watch('actionType')==='reverify'?'Reverification':'Delinking'} Failed, Please try again !!`,
          variant: "error",
          duration: 10000
        });
        setIsVerified(false);
        setVerificationStatus(false);
      }
    }).catch(err => {
      setEntityDetails(null);
      setIsVerified(false);
      setVerificationStatus(false);
      toast({
        title: "",
        description: `${watch('actionType')==='reverify'?'Reverification':'Delinking'} Failed, Please try again !!`,
        variant: "error",
        duration: 10000
      });

    }).finally(() => { setIsVerifying(false); })
  };
  const handleSubmit1 = async () => {
    if (!declaration) {
      alert("Please accept the declaration to continue.");
      return;
    }
    let actionType = watch('actionType');
    const apiHandlers = {
      reverify: reVerifyParticipant,
      delink: delinkParticipant,
    };
    setIsSubmitting(true);
    const apiCall = apiHandlers[actionType] || apiHandlers.reverify;
    const payload = {
      TIN: entityDetails?.TIN,
      effectiveDate: entityDetails?.effectiveDate,
      email: entityWatch('email'),
      emarataxToken: entityDetails?.emarataxToken,
      entityNameAr: entityDetails?.entityNameAr,
      entityNameEn: entityDetails?.entityNameEn,
      orgName: entityDetails?.entityNameEn,
      legalType: entityDetails?.legalType,
      mobile: entityWatch('mobile'),
      address: "UAE",
      peppolId: `0235:${entityDetails?.TIN}`,
      countryCode: entityDetails?.countryCode || "AE",
      vatTrn: entityDetails?.vatTrn,
    };
    try {
      const res = await apiCall(payload);
      if (res.data.status === "FAILED") {
        const sequence = res?.data?.sequence;
        switch (sequence) {
          case '1':
            setStepsStatus({ step1: 'failed', step2: '' });
            break;
          case '2':
            setStepsStatus({ step1: 'completed', step2: 'failed' });
            break;
          default:
        }
        setOnBoardingSuccess({ success: false, failure: true });
      } else {
        setStepsStatus({ step1: 'completed', step2: 'completed' });
        setOnBoardingSuccess({ success: true, failure: false });
      }
    } catch {
      setStepsStatus({ step1: 'failed', step2: 'failed' });
      setOnBoardingSuccess({ success: false, failure: true });
    }
    finally {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }
    dismiss();

  };
  //email and mobile in editing mode
  return (
    <div className="min-h-screen bg-background">
      <PortalHeader text={'Reverifying / Delinking Request'} description={'Please confirm details mentioned and submit your request'} />

      <main className="container mx-auto px-6 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="bg-card rounded-xl shadow-lg border border-border p-8 space-y-8">
            <h2 className="text-2xl font-bold text-foreground text-center border-b border-border pb-4">
              Enter Applicant Details
            </h2>

            <Form {...form}>
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Registered EmaraTax Email Address <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="example@company.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="TIN"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        TIN (Tax Identifier Number) <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder=""
                          maxLength={10}
                          {...field}
                          disabled
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="actionType"
                  defaultValue="reverify"
                  render={({ field }) => (
                    <FormItem className="space-y-3 mt-4">
                      <FormLabel>Select Option</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value || "reverify"}
                          className="flex flex-row gap-6"
                          disabled={isVerifying || isVerified}
                        >
                          <FormItem className="flex items-center space-x-2 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="reverify" id="reverify" />
                            </FormControl>
                            <FormLabel className="font-normal" htmlFor="reverify">Reverify</FormLabel>
                          </FormItem>

                          <FormItem className="flex items-center space-x-2 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="delink" id="delink" />
                            </FormControl>
                            <FormLabel className="font-normal" htmlFor="delink">Delink</FormLabel>
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-center pt-4">
                  <Button
                    type="button"
                    onClick={(data) => handleSubmit(handleVerify)(data)}
                    disabled={isVerifying || isVerified || isVerifyDisabled}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base font-semibold rounded-lg shadow-md transition-all hover:shadow-lg disabled:opacity-50"
                  >
                    {isVerifying ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Verifying...
                      </>
                    ) : isVerified ? (
                      "Verified ✓"
                    ) : (
                      "Verify Applicant Details"
                    )}
                  </Button>
                </div>
              </div>
            </Form>

            {isVerified && (
              <div className="space-y-6 pt-6 border-t border-border">
                {watch('actionType') === 'reverify' ?
                  <div>
                    <h2 className="text-xl font-bold text-foreground text-center mb-4">
                      Reverification - Entity Details
                    </h2>
                    <Form {...entityDetailsForm}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={entityDetailsForm.control}
                          name="TIN"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>TIN (Tax Identifier Number)</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder=""
                                  {...field}
                                  value={entityDetails?.TIN || ""}
                                  disabled
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={entityDetailsForm.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Registered EmaraTax Email Address</FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="example@company.com"
                                  {...field}
                                  className='bg-[hsl(240deg_73.33%_97.06%)]'
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={entityDetailsForm.control}
                          name="legalType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Legal Type</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder=""
                                  {...field}
                                  value={entityDetails?.legalType || ""}
                                  disabled
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={entityDetailsForm.control}
                          name="entityNameEn"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Entity Legal Name in English</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder=""
                                  {...field}
                                  value={entityDetails?.entityNameEn || ""}
                                  disabled
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={entityDetailsForm.control}
                          name="entityNameAr"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Entity Legal Name in Arabic</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder=""
                                  {...field}
                                  value={entityDetails?.entityNameAr || ""}
                                  disabled
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={entityDetailsForm.control}
                          name="vatTrn"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>VAT TRN</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder=""
                                  {...field}
                                  value={entityDetails?.vatTrn || ""}
                                  disabled
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={entityDetailsForm.control}
                          name="effectiveDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                Effective Date of Registration / Joining VAT Group
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder=""
                                  {...field}
                                  value={entityDetails?.effectiveDate || ""}
                                  disabled
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={entityDetailsForm.control}
                          name="mobile"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Registered EmaraTax Mobile Number</FormLabel>
                              <FormControl>
                                <Input
                                  type="tel"
                                  placeholder="0501234567"
                                  maxLength={10}
                                  {...field}
                                  className="bg-[hsl(240deg_73.33%_97.06%)]"

                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {((entityDetails?.verificationStatus === "True" &&
                          watch("actionType") == "reverify") ||
                          (entityDetails?.verificationStatus === "False" &&
                            !watch("actionType"))) && (
                            <FormField
                              control={entityDetailsForm.control}
                              name="msgDesc"
                              render={({ field }) => (
                                <FormItem className="sm:col-span-2">
                                  <FormLabel>Message Description</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder=""
                                      {...field}
                                      value={entityDetails?.msgDesc || ""}
                                      disabled
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          )}
                      </div>
                    </Form>

                  </div>
                  :
                  <div>
                    <EntityDetailsSection details={entityDetails} actionType={watch('actionType')} />
                  </div>
                }
                {isVerified && verificationStatus &&
                  <>
                    <div className="flex items-start gap-3 pt-4">
                      <Checkbox
                        id="declaration"
                        checked={declaration}
                        onCheckedChange={(checked) => setDeclaration(checked as boolean)}
                        className="mt-1"
                      />
                      <Label
                        htmlFor="declaration"
                        className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                      >
                        I declare that all information provided is true, accurate and complete to the best of my knowledge and belief.
                      </Label>
                    </div>
                    <div className="flex justify-center pt-4">
                      <Button
                        type="button"
                        onClick={handleSubmit1}
                        disabled={!declaration || isSubmitting || isSubmitted || isSubmitDisabled}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base font-semibold rounded-lg shadow-md transition-all hover:shadow-lg disabled:opacity-50"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            {watch('actionType') === 'reverify' ? 'Reverifying...' : 'Delinking...'}
                          </>
                        ) : isSubmitted ? (
                          onBoardingSuccess.success && !onBoardingSuccess.failure ? watch('actionType') === 'reverify' ? 'Reverified ✓' : 'Delinked ✓' : 'Submit'
                        ) : (
                          "Submit"
                        )}
                      </Button>
                    </div>
                  </>
                }
                {isSubmitted && onBoardingSuccess.success && !onBoardingSuccess.failure && (
                  <SuccessMessage type={watch('actionType')} />
                )}

                {isSubmitted && !onBoardingSuccess.success && onBoardingSuccess.failure && (
                  <FailureMessage message={watch('actionType')} />
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReverifyingOrDeLinkingRequest;
